import { getLocale } from "../../i18n";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';
import '../../assets/css/trace.css';

// ── Helpers ────────────────────────────────────────────────────────────────

const HOUR_PX = 72;
const APP_COLORS = ['#8b5cf6', '#4b2a73', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#64748b', '#14b8a6'];

// Familiar apps keep a recognisable colour; anything else gets a stable colour from its name.
const KNOWN_APP_COLORS = {
  'google chrome': '#4c85ea', chrome: '#4c85ea', safari: '#38a3d8', firefox: '#e2762d', arc: '#7c5cff',
  terminal: '#3d4a5c', iterm2: '#3d4a5c', 'visual studio code': '#7660f7', code: '#7660f7', cursor: '#5b5fc7',
  phpstorm: '#e0407b', webstorm: '#22a5c9', slack: '#cf4e7d', 'microsoft outlook': '#e8a03a', outlook: '#e8a03a',
  msteams: '#5b5fc7', 'microsoft teams': '#5b5fc7', figma: '#55b685', claude: '#d97757', codex: '#10a37f',
  textedit: '#8b93a1', 'adobe lightroom': '#3aa0d8', signal: '#3a76f0', zoom: '#2d8cff',
};

function appColor(name = '') {
  const known = KNOWN_APP_COLORS[String(name).toLowerCase()];
  if (known) return known;
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return APP_COLORS[hash % APP_COLORS.length];
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function minutesOfDay(date) {
  const d = new Date(date);
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

function fmtDuration(seconds) {
  const mins = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

function fmtClock(date) {
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// "09:30" on the same local day as `base` -> Date
function clockToDate(clock, base) {
  const [h, m] = String(clock).split(':').map(Number);
  const d = new Date(base);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
}

function isSameDay(a, b) {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

function fmtDayLabel(date) {
  const today = startOfDay(new Date());
  if (isSameDay(date, today)) return tr("Today");
  if (isSameDay(date, addDays(today, -1))) return tr("Yesterday");
  return date.toLocaleDateString(getLocale(), { weekday: 'short', day: 'numeric', month: 'short' });
}

// Real activity flips between apps every minute or two, so per-app blocks look scattered.
// Instead, activity with short gaps becomes one work session; the apps used are summarised.
const SESSION_GAP_MIN = 15;

// A short break shorter than the session merge gap still splits the session in two if left
// alone; instead it's shown as a marker inside the merged session. `idle` is time away from
// the keyboard, `excluded` is time on a personal/social app or site (content-free by design).
function buildSessions(blocks, idle = [], excluded = []) {
  const sorted = blocks
    .map((b) => ({ ...b, start: new Date(b.start), end: new Date(b.end) }))
    .sort((x, y) => x.start - y.start);
  const sessions = [];
  sorted.forEach((b) => {
    const last = sessions[sessions.length - 1];
    if (last && (b.start - last.end) / 60000 <= SESSION_GAP_MIN) {
      if (b.end > last.end) last.end = b.end;
      last.seconds += b.seconds;
      last.apps.set(b.app_name, (last.apps.get(b.app_name) || 0) + b.seconds);
      if (b.domain) last.domains.set(b.domain, (last.domains.get(b.domain) || 0) + b.seconds);
      if (!last.project_name && b.project_name) last.project_name = b.project_name;
      return;
    }
    sessions.push({
      start: b.start,
      end: b.end,
      seconds: b.seconds,
      apps: new Map([[b.app_name, b.seconds]]),
      domains: new Map(b.domain ? [[b.domain, b.seconds]] : []),
      project_name: b.project_name || null,
    });
  });
  const idleRanges = idle.map((i) => ({ start: new Date(i.start), end: new Date(i.end) }));
  const excludedRanges = excluded.map((i) => ({ start: new Date(i.start), end: new Date(i.end) }));
  const overlapSeconds = (ranges, session) => ranges.reduce((sum, r) => {
    const overlapStart = Math.max(r.start, session.start);
    const overlapEnd = Math.min(r.end, session.end);
    return overlapEnd > overlapStart ? sum + (overlapEnd - overlapStart) / 1000 : sum;
  }, 0);
  return sessions.map((x) => {
    const apps = [...x.apps.entries()].sort((p, q) => q[1] - p[1]);
    const domains = [...x.domains.entries()].sort((p, q) => q[1] - p[1]);
    const awaySeconds = overlapSeconds(idleRanges, x);
    const excludedSeconds = overlapSeconds(excludedRanges, x);
    return { ...x, app_name: apps[0][0], topApps: apps, topDomains: domains, awaySeconds, excludedSeconds };
  });
}

function errorMessage(err, fallback) {
  return err?.response?.data?.errors?.[0]?.msg || fallback;
}

// ── Loading skeleton (same shimmer bars as the timer page) ─────────────────

function SkBar({ width, height = 12, style = {} }) {
  useTranslation();
  return <div className="sk-bar" style={{ width, height, borderRadius: 6, display: 'block', ...style }} />;
}

function TraceSkeleton() {
  useTranslation();
  const capturedBars = [[26, 46], [96, 70], [196, 34], [262, 96], [388, 40]];
  const timesheetBars = [[60, 130], [236, 92]];
  return (
    <div role="status" aria-busy="true" aria-label={tr("Loading your day")}>
      <div className="trc-stats">
        <div className="trc-stat trc-stat--hero" style={{ opacity: 0.55 }}>
          <SkBar width={70} height={10} style={{ marginBottom: 10 }} />
          <SkBar width={96} height={26} style={{ borderRadius: 8, marginBottom: 10 }} />
          <SkBar width={80} height={9} />
        </div>
        {[0, 1, 2].map((i) => (
          <div className="trc-stat" key={i}>
            <SkBar width={84} height={10} style={{ marginBottom: 10 }} />
            <SkBar width={72} height={26} style={{ borderRadius: 8, marginBottom: 10 }} />
            <SkBar width={100} height={9} />
          </div>
        ))}
      </div>

      <div className="trc-grid">
        <section className="trc-card">
          <div className="trc-card-head">
            <div>
              <SkBar width={90} height={14} style={{ marginBottom: 7 }} />
              <SkBar width={220} height={10} />
            </div>
          </div>
          <div className="trc-apps">
            {[110, 86, 74, 92].map((w, i) => <SkBar key={i} width={w} height={26} style={{ borderRadius: 999 }} />)}
          </div>
          <div className="trc-sk-tl">
            <div className="trc-sk-col">
              {capturedBars.map(([top, h]) => (
                <SkBar key={top} width="100%" height={h} style={{ position: 'absolute', top, left: 0, borderRadius: 9 }} />
              ))}
            </div>
            <div className="trc-sk-col">
              {timesheetBars.map(([top, h]) => (
                <SkBar key={top} width="100%" height={h} style={{ position: 'absolute', top, left: 0, borderRadius: 9 }} />
              ))}
            </div>
          </div>
        </section>

        <aside className="trc-side">
          <section className="trc-card trc-review">
            <SkBar width={130} height={11} style={{ marginBottom: 10 }} />
            <SkBar width={96} height={22} style={{ borderRadius: 999, marginBottom: 20 }} />
            <SkBar width={80} height={9} style={{ marginBottom: 8 }} />
            <SkBar width="100%" height={38} style={{ borderRadius: 10, marginBottom: 18 }} />
            <SkBar width={60} height={9} style={{ marginBottom: 8 }} />
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              {[84, 110, 96].map((w, i) => <SkBar key={i} width={w} height={32} style={{ borderRadius: 10 }} />)}
            </div>
            <SkBar width="100%" height={38} style={{ borderRadius: 10, marginBottom: 16 }} />
            <SkBar width="100%" height={40} style={{ borderRadius: 11 }} />
          </section>
          <section className="trc-card">
            <div className="trc-card-head"><SkBar width={70} height={14} /></div>
            <div style={{ padding: 14, display: 'grid', gap: 14 }}>
              {[0, 1, 2].map((i) => (
                <div key={i}>
                  <SkBar width="72%" height={12} style={{ marginBottom: 7 }} />
                  <SkBar width="46%" height={9} />
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function TracePage() {
  useTranslation();
  const auth = useSelector((s) => s.auth);
  const [day, setDay] = useState(() => startOfDay(new Date()));
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [manual, setManual] = useState(null); // time range the user picked on the timeline
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const [resizing, setResizing] = useState(null); // 'start' | 'end' while dragging a selection edge
  const [moving, setMoving] = useState(null); // { mouseDownMinute, startMin, endMin } while dragging the whole block
  const geomRef = useRef({});
  const capturedColRef = useRef(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  // Apps switched off in the filter bar. View-only: nothing is deleted or stopped from capturing.
  const [hiddenApps, setHiddenApps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trc_hidden_apps') || '[]'); } catch (e) { return []; }
  });
  const shownCaptured = useMemo(
    () => (data ? data.captured.filter((b) => !hiddenApps.includes(b.app_name)) : []),
    [data, hiddenApps]
  );

  const config = useMemo(
    () => ({ headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' } }),
    [auth.token]
  );

  const showToast = useCallback((message) => {
    setToast(message);
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => setToast(''), 3600);
  }, []);

  const loadDay = useCallback(async (silent = false) => {
    if (!auth.workspace_id) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const from = startOfDay(day);
      const to = addDays(from, 1);
      const res = await axios.get('/api/time-suggestions/day', {
        ...config,
        params: { workspace_id: auth.workspace_id, from: from.toISOString(), to: to.toISOString() },
      });
      setData(res.data);
      setSelectedId((current) =>
        current === 'manual' || res.data.drafts.some((d) => d.id === current) ? current : res.data.drafts[0]?.id ?? null
      );
    } catch (err) {
      setError(errorMessage(err, 'Could not load this day. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, config, day]);

  useEffect(() => { loadDay(); }, [loadDay]);

  // Keep the "now" line and today's captured activity fresh without a manual reload.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setTick((n) => n + 1);
      if (isSameDay(day, new Date()) && document.visibilityState === 'visible') loadDay(true);
    }, 60000);
    return () => clearInterval(id);
  }, [day, loadDay]);
  useEffect(() => { setManual(null); }, [day]);

  useEffect(() => {
    if (!auth.workspace_id) return;
    axios
      .post(
        '/api/project/search',
        JSON.stringify({
          workspace_id: auth.workspace_id,
          is_workspace_admin: auth.is_workspace_admin,
          is_archived: 0,
          searchTerm: '',
        }),
        config
      )
      .then((res) => setProjects(res.data.results || []))
      .catch(() => setProjects([]));
  }, [auth.workspace_id, auth.is_workspace_admin, config]);

  // ── Drafts with the user's local edits applied ──
  const drafts = useMemo(() => {
    if (!data) return [];
    return data.drafts.map((d) => {
      const edit = edits[d.id] || {};
      const origStart = new Date(d.suggested_start);
      const origEnd = new Date(d.suggested_end);
      const start = edit.start ? clockToDate(edit.start, origStart) : origStart;
      const end = edit.end ? clockToDate(edit.end, origEnd) : origEnd;
      const projectId = edit.projectId !== undefined ? edit.projectId : d.suggested_project_id;
      const title = edit.title !== undefined ? edit.title : d.suggested_title || '';
      const validRange = end > start && start >= origStart && end <= origEnd && end - start >= 60000;
      const ready = Boolean(projectId) && title.trim().length >= 3 && validRange;
      return { ...d, origStart, origEnd, start, end, projectId, title, validRange, ready };
    });
  }, [data, edits]);

  // A time range picked on the timeline (click a captured block or drag), shaped like a draft.
  const manualDraft = useMemo(() => {
    if (!manual || !data) return null;
    const edit = edits.manual || {};
    const origStart = manual.start;
    const origEnd = manual.end;
    const start = edit.start ? clockToDate(edit.start, origStart) : origStart;
    const end = edit.end ? clockToDate(edit.end, origEnd) : origEnd;
    const appSecs = new Map();
    const domSecs = new Map();
    const titleSecs = new Map();
    shownCaptured.forEach((b) => {
      const sec = (Math.min(new Date(b.end), origEnd) - Math.max(new Date(b.start), origStart)) / 1000;
      if (sec <= 0) return;
      appSecs.set(b.app_name, (appSecs.get(b.app_name) || 0) + sec);
      if (b.domain) domSecs.set(b.domain, (domSecs.get(b.domain) || 0) + sec);
      if (b.window_title) titleSecs.set(b.window_title, (titleSecs.get(b.window_title) || 0) + sec);
    });
    const top_apps = [...appSecs.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4).map(([app_name, seconds]) => ({ app_name, seconds }));
    const top_domains = [...domSecs.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([domain, seconds]) => ({ domain, seconds }));
    const top_window_titles = [...titleSecs.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([window_title, seconds]) => ({ window_title, seconds }));
    const defaultTitle = top_apps.length ? `Work in ${top_apps.slice(0, 2).map((a) => a.app_name).join(' and ')}` : 'Work session';
    const title = edit.title !== undefined ? edit.title : defaultTitle;
    const projectId = edit.projectId !== undefined ? edit.projectId : null;
    const validRange = end > start && end - start >= 60000 && start >= startOfDay(day) && end.getTime() <= Date.now() + 60000;
    const ready = Boolean(projectId) && title.trim().length >= 3 && validRange;
    return { id: 'manual', isManual: true, origStart, origEnd, start, end, top_apps, top_domains, top_window_titles, title, projectId, validRange, ready, confidence_score: null, suggested_project_id: null };
  }, [manual, data, edits, day]);

  const selected = (selectedId === 'manual' ? manualDraft : drafts.find((d) => d.id === selectedId)) || null;
  const readyDrafts = drafts.filter((d) => d.ready);

  const setEdit = (id, patch) => setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const selectRange = (start, end) => {
    setEdits((prev) => { const { manual: _drop, ...rest } = prev; return rest; });
    setManual({ start: new Date(start), end: new Date(end) });
    setSelectedId('manual');
  };

  const clearManual = () => {
    setManual(null);
    setEdits((prev) => { const { manual: _drop, ...rest } = prev; return rest; });
    setSelectedId(drafts[0]?.id ?? null);
  };

  // ── Timeline window ──
  const { winStart, winEnd } = useMemo(() => {
    const points = [];
    if (data) {
      shownCaptured.forEach((b) => points.push(minutesOfDay(b.start), minutesOfDay(b.end)));
      data.logged.forEach((l) => points.push(minutesOfDay(l.start), minutesOfDay(l.end)));
      drafts.forEach((d) => points.push(minutesOfDay(d.origStart), minutesOfDay(d.origEnd)));
    }
    let lo = points.length ? Math.floor(Math.min(...points) / 60) * 60 : 8 * 60;
    let hi = points.length ? Math.ceil(Math.max(...points) / 60) * 60 : 18 * 60;
    lo = Math.max(0, lo - 60);
    hi = Math.min(24 * 60, hi + 60);
    // Today: the future is empty, so stop the timeline shortly after "now".
    if (isSameDay(day, new Date())) hi = Math.min(hi, Math.ceil((minutesOfDay(new Date()) + 20) / 60) * 60);
    if (hi - lo < 4 * 60) hi = Math.min(24 * 60, lo + 4 * 60);
    return { winStart: lo, winEnd: hi };
  }, [data, shownCaptured, drafts, day]);

  const top = (date) => ((minutesOfDay(date) - winStart) / 60) * HOUR_PX;
  // Timesheet blocks shorter than TALL_ENOUGH show only their title, on one line.
  const TALL_MIN = 22;
  const TALL_ENOUGH = 40;
  const height = (start, end, min = 14) => Math.max(((minutesOfDay(end) - minutesOfDay(start)) / 60) * HOUR_PX - 2, min);

  // Captured activity is drawn as work sessions at their real time and length. Sessions are
  // separated by more than SESSION_GAP_MIN, so they never overlap and need no side-by-side lanes.
  const capturedBlocks = useMemo(() => {
    if (!data) return [];
    const MIN_H = 18;
    const nowPx = isSameDay(day, new Date()) ? ((minutesOfDay(new Date()) - winStart) / 60) * HOUR_PX : null;
    return buildSessions(shownCaptured, data.idle || [], data.excluded || []).map((session) => {
      const t = ((minutesOfDay(session.start) - winStart) / 60) * HOUR_PX;
      const full = Math.max(((minutesOfDay(session.end) - minutesOfDay(session.start)) / 60) * HOUR_PX - 2, MIN_H);
      return { ...session, t, h: nowPx !== null && t + full > nowPx ? Math.max(MIN_H, nowPx - t) : full };
    });
  }, [data, shownCaptured, winStart, day]);

  // ── Stats ──
  const capturedSeconds = data ? shownCaptured.reduce((sum, b) => sum + b.seconds, 0) : 0;
  const loggedSeconds = data ? data.logged.reduce((sum, l) => sum + (new Date(l.end) - new Date(l.start)) / 1000, 0) : 0;
  const draftedSeconds = drafts.reduce((sum, d) => sum + (d.end - d.start) / 1000, 0);
  const appNames = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.captured.map((b) => b.app_name))];
  }, [data]);

  // ── Actions ──
  const logDraft = async (draft, { silent = false } = {}) => {
    const project = projects.find((p) => p.id === draft.projectId);
    const trimmed = draft.start.getTime() !== draft.origStart.getTime() || draft.end.getTime() !== draft.origEnd.getTime();
    await axios.post(
      `/api/time-suggestions/${draft.id}/accept`,
      JSON.stringify({
        workspace_id: auth.workspace_id,
        title: draft.title.trim(),
        project_id: draft.projectId,
        client_id: project?.client_id || null,
        ...(trimmed ? { start: draft.start.toISOString(), end: draft.end.toISOString() } : {}),
      }),
      config
    );
    if (!silent) {
      const signal = draft.top_domains?.[0]?.domain || draft.top_apps?.[0]?.app_name;
      const remember = signal && project ? ` Trace will remember ${signal} → ${project.project_name}.` : '';
      showToast(`Logged ${fmtDuration((draft.end - draft.start) / 1000)}${project ? ` to ${project.project_name}` : ''}.${remember}`);
    }
  };

  const logManual = async (entry) => {
    const project = projects.find((p) => p.id === entry.projectId);
    await axios.post(
      '/api/time-suggestions/log-range',
      JSON.stringify({
        workspace_id: auth.workspace_id,
        title: entry.title.trim(),
        project_id: entry.projectId,
        client_id: project?.client_id || null,
        start: entry.start.toISOString(),
        end: entry.end.toISOString(),
      }),
      config
    );
    const signal = entry.top_domains?.[0]?.domain || entry.top_apps?.[0]?.app_name;
    const remember = signal && project ? ` Trace will remember ${signal} → ${project.project_name}.` : '';
    showToast(`Logged ${fmtDuration((entry.end - entry.start) / 1000)}${project ? ` to ${project.project_name}` : ''}.${remember}`);
  };

  const handleLog = async () => {
    if (!selected || !selected.ready || busy) return;
    setBusy(true);
    try {
      if (selected.isManual) {
        await logManual(selected);
        clearManual();
      } else {
        await logDraft(selected);
      }
      await loadDay();
    } catch (err) {
      showToast(errorMessage(err, 'Could not log this block. Please try again.'));
      // A draft that failed because its time is already logged is retired server-side.
      await loadDay().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const handleLogAllReady = async () => {
    if (!readyDrafts.length || busy) return;
    setBusy(true);
    let done = 0;
    let seconds = 0;
    try {
      for (const draft of readyDrafts) {
        // eslint-disable-next-line no-await-in-loop
        await logDraft(draft, { silent: true });
        done += 1;
        seconds += (draft.end - draft.start) / 1000;
      }
      showToast(`Logged ${done} ${done === 1 ? 'entry' : 'entries'} · ${fmtDuration(seconds)}.`);
    } catch (err) {
      showToast(errorMessage(err, `Logged ${done}, then something failed. Please review the rest.`));
    } finally {
      setBusy(false);
      await loadDay();
    }
  };

  const handleDismiss = async () => {
    if (!selected || busy) return;
    if (selected.isManual) { clearManual(); return; }
    setBusy(true);
    try {
      await axios.post(
        `/api/time-suggestions/${selected.id}/dismiss`,
        JSON.stringify({ workspace_id: auth.workspace_id }),
        config
      );
      showToast('Dismissed. Trace will suggest fewer blocks like this.');
      await loadDay();
    } catch (err) {
      showToast(errorMessage(err, 'Could not dismiss this block.'));
    } finally {
      setBusy(false);
    }
  };

  // ── Picking a time range on the Captured column ──
  const minuteAt = (clientY) => {
    const rect = capturedColRef.current.getBoundingClientRect();
    return Math.round(winStart + ((clientY - rect.top) / HOUR_PX) * 60);
  };
  const dateAtMinute = (m) => { const d = startOfDay(day); d.setMinutes(m); return d; };
  const nowMinute = isSameDay(day, new Date()) ? Math.floor(minutesOfDay(new Date())) : 24 * 60;

  const onCapturedDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { y0: e.clientY, m0: minuteAt(e.clientY), moved: false, target: e.target };
  };
  const onCapturedMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved && Math.abs(e.clientY - d.y0) < 6) return;
    d.moved = true;
    setDrag({ a: d.m0, b: minuteAt(e.clientY) });
  };
  const onCapturedUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d) return;
    if (d.moved) {
      const a = Math.max(0, Math.min(d.m0, minuteAt(e.clientY)));
      const b = Math.min(nowMinute, Math.max(d.m0, minuteAt(e.clientY)));
      if (b - a >= 1) selectRange(dateAtMinute(a), dateAtMinute(b));
      else showToast('Pick at least 1 minute, and not in the future.');
    } else {
      const el = d.target.closest && d.target.closest('.trc-block--app');
      const block = el ? capturedBlocks[Number(el.dataset.idx)] : null;
      if (block) selectRange(block.start, block.end);
    }
  };
  const onCapturedLeave = () => { dragRef.current = null; setDrag(null); };

  // Latest geometry helpers for the window-level resize listeners below.
  geomRef.current = { minuteAt, dateAtMinute, nowMinute };

  // Dragging the top/bottom edge of the selection changes its start/end.
  useEffect(() => {
    if (!resizing) return undefined;
    const move = (e) => {
      const { minuteAt: at, dateAtMinute: dateAt, nowMinute: nowMin } = geomRef.current;
      const m = at(e.clientY);
      setManual((prev) => {
        if (!prev) return prev;
        if (resizing === 'start') {
          const next = Math.max(0, Math.min(m, Math.floor(minutesOfDay(prev.end)) - 1));
          return { ...prev, start: dateAt(next) };
        }
        const next = Math.min(nowMin, Math.max(m, Math.ceil(minutesOfDay(prev.start)) + 1));
        return { ...prev, end: dateAt(next) };
      });
      // The handles are the source of truth now; drop any typed times.
      setEdits((prev) => {
        if (!prev.manual) return prev;
        const { start: _s, end: _e, ...restEdit } = prev.manual;
        return { ...prev, manual: restEdit };
      });
    };
    const up = () => setResizing(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [resizing]);

  // Dragging the block itself shifts start and end together, keeping the duration fixed.
  useEffect(() => {
    if (!moving) return undefined;
    const move = (e) => {
      const { minuteAt: at, dateAtMinute: dateAt, nowMinute: nowMin } = geomRef.current;
      const delta = at(e.clientY) - moving.mouseDownMinute;
      const duration = moving.endMin - moving.startMin;
      const nextStart = Math.max(0, Math.min(moving.startMin + delta, nowMin - duration));
      setManual((prev) => (prev ? { ...prev, start: dateAt(nextStart), end: dateAt(nextStart + duration) } : prev));
      // Moving the whole block is the source of truth now; drop any typed times.
      setEdits((prev) => {
        if (!prev.manual) return prev;
        const { start: _s, end: _e, ...restEdit } = prev.manual;
        return { ...prev, manual: restEdit };
      });
    };
    const up = () => setMoving(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [moving]);

  // Shrink the selection to the first/last captured activity inside it.
  const fitToActivity = () => {
    if (!manual || !data) return;
    const inside = shownCaptured
      .map((b) => ({ start: new Date(b.start), end: new Date(b.end) }))
      .filter((b) => b.end > manual.start && b.start < manual.end);
    if (!inside.length) { showToast('No captured activity in this range.'); return; }
    const start = new Date(Math.max(manual.start, Math.min(...inside.map((b) => b.start))));
    const end = new Date(Math.min(manual.end, Math.max(...inside.map((b) => b.end))));
    if (end - start < 60000) return;
    setEdits((prev) => { const { manual: _drop, ...rest } = prev; return rest; });
    setManual({ start, end });
  };

  const startResize = (edge) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = null;
    setResizing(edge);
  };

  const startMove = (e) => {
    if (!manual) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = null;
    setMoving({
      mouseDownMinute: geomRef.current.minuteAt(e.clientY),
      startMin: minutesOfDay(manual.start),
      endMin: minutesOfDay(manual.end),
    });
  };

  const toggleApp = (appName) => {
    setHiddenApps((prev) => {
      const next = prev.includes(appName) ? prev.filter((n) => n !== appName) : [...prev, appName];
      try { localStorage.setItem('trc_hidden_apps', JSON.stringify(next)); } catch (e) { /* view preference only */ }
      return next;
    });
  };

  const hourMarks = [];
  for (let m = winStart; m <= winEnd; m += 60) hourMarks.push(m);
  const timelineHeight = ((winEnd - winStart) / 60) * HOUR_PX;
  const isToday = isSameDay(day, new Date());
  const nowTop = isToday ? ((minutesOfDay(new Date()) - winStart) / 60) * HOUR_PX : null;

  return (
    <div>
      <Sidebar />
      <div className="trc-page">
        {/* ── Header ── */}
        <div className="trc-header">
          <div>
            <span className="trc-eyebrow">Minutezilla Trace</span>
            <h1 className="trc-title">{tr("Your day, remembered")}</h1>
            <p className="trc-subtitle">{tr("Trace watched quietly and drafted your timesheet. Review, tweak, log.")}</p>
          </div>
          <div className="trc-header-right">
            <span className="trc-chip trc-chip--private">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>{tr("Private to you")} </span>
            <div className="trc-pager">
              <button type="button" aria-label={tr("Previous day")} onClick={() => setDay(addDays(day, -1))}>‹</button>
              <span>{fmtDayLabel(day)}</span>
              <button type="button" aria-label={tr("Next day")} disabled={isToday} onClick={() => setDay(addDays(day, 1))}>›</button>
            </div>
          </div>
        </div>

        {loading && !data ? (
          <TraceSkeleton />
        ) : (
          <>
        {/* ── Stats ── */}
        <div className="trc-stats">
          <div className="trc-stat trc-stat--hero">
            <span className="trc-stat-label">{tr("Captured")}</span>
            <strong>{fmtDuration(capturedSeconds)}</strong>
            <small>{appNames.length} {appNames.length === 1 ? tr("app") : tr("apps")} {tr("seen")}</small>
          </div>
          <div className="trc-stat">
            <span className="trc-stat-label">{tr("Logged")}</span>
            <strong>{fmtDuration(loggedSeconds)}</strong>
            <div className="trc-bar"><i style={{ width: `${Math.min(100, (loggedSeconds / Math.max(capturedSeconds, 1)) * 100)}%` }} /></div>
          </div>
          <div className="trc-stat">
            <span className="trc-stat-label">{tr("Drafted by Trace")}</span>
            <strong>{fmtDuration(draftedSeconds)}</strong>
            <small>{drafts.length} {drafts.length === 1 ? tr("block") : tr("blocks")} {tr("waiting")}</small>
          </div>
          <div className="trc-stat">
            <span className="trc-stat-label">{tr("Ready in one click")}</span>
            <strong>{readyDrafts.length}</strong>
            <small>{tr("project set")}</small>
          </div>
        </div>

        {error && <div className="trc-error">{error}</div>}

        <div className={`trc-grid${loading ? ' is-loading' : ''}`}>
          {/* ── Timeline ── */}
          <section className="trc-card">
            <div className="trc-card-head">
              <div>
                <h2>{tr("Timeline")}</h2>
                <p>{tr("Left: what Trace captured · Right: your timesheet")}</p>
              </div>
              <small>{tr("Click a draft, click a captured block, or drag to log time")}</small>
            </div>

            {data && appNames.length > 0 && (
              <div className="trc-apps">
                {appNames.map((name) => {
                  const off = hiddenApps.includes(name);
                  return (
                    <button
                      type="button"
                      key={name}
                      className={`trc-app${off ? ' trc-app--off' : ''}`}
                      onClick={() => toggleApp(name)}
                      title={off ? tr("Show this app again") : tr("Hide this app from the timeline")}
                    >
                      <i style={{ background: appColor(name) }} />{name}
                    </button>
                  );
                })}
                <span className="trc-apps-hint">{tr("tap to hide an app")}</span>
              </div>
            )}

            {loading && !data ? (
              <div className="trc-empty">{tr("Loading your day…")}</div>
            ) : data && data.captured.length === 0 && data.logged.length === 0 && drafts.length === 0 ? (
              <div className="trc-empty">
                <b>{tr("Nothing captured for")} {fmtDayLabel(day).toLowerCase()}</b>{tr("Make sure the Minutezilla Trace desktop app is running and tracking is on.")} <Link to="/desktop-app" className="trc-link">{tr("Set up the desktop app")}</Link>
              </div>
            ) : (
              <div className="trc-tl">
                <div className="trc-tl-col trc-tl-col--hours" style={{ height: timelineHeight }}>
                  {hourMarks.map((m) => (
                    <span key={m} style={{ top: ((m - winStart) / 60) * HOUR_PX }}>{String(m / 60).padStart(2, '0')}:00</span>
                  ))}
                </div>
                <div
                  className="trc-tl-col trc-tl-col--captured"
                  style={{ height: timelineHeight }}
                  ref={capturedColRef}
                  onMouseDown={onCapturedDown}
                  onMouseMove={onCapturedMove}
                  onMouseUp={onCapturedUp}
                  onMouseLeave={onCapturedLeave}
                >
                  <div className="trc-tl-title">{tr("Captured")}</div>
                  {capturedBlocks.map((b, idx) => {
                    const appList = b.topApps.map(([name]) => name);
                    const sites = b.topDomains.slice(0, 2).map(([d]) => d);
                    const title = appList.length > 2 ? `${appList.slice(0, 2).join(', ')} +${appList.length - 2}` : appList.join(', ');
                    const detail = [fmtDuration(b.seconds), b.project_name, ...sites].filter(Boolean).join(' · ');
                    const tooltip = [
                      `${fmtClock(b.start)} – ${fmtClock(b.end)} · ${fmtDuration(b.seconds)} active`,
                      ...(b.awaySeconds >= 60 ? [`Away ${Math.round(b.awaySeconds / 60)} min`] : []),
                      ...(b.excludedSeconds >= 60 ? [`Personal · not tracked ${Math.round(b.excludedSeconds / 60)} min`] : []),
                      ...b.topApps.map(([name, secs]) => `${name} · ${fmtDuration(secs)}`),
                      ...b.topDomains.slice(0, 5).map(([d, secs]) => `${d} · ${fmtDuration(secs)}`),
                    ].join('\n');
                    return (
                      <div
                        key={`${b.start.toISOString()}`}
                        className={`trc-block trc-block--app${b.h < 28 ? ' trc-block--thin' : ''}`}
                        data-idx={idx}
                        style={{ top: b.t, height: b.h, background: appColor(b.app_name), left: 8, right: 8 }}
                        title={`${tooltip}\nClick to log this time`}
                      >
                        <b>{title}{b.h < 44 ? ` · ${fmtDuration(b.seconds)}` : ''}</b>
                        {b.h >= 44 && <span>{detail}</span>}
                        {b.awaySeconds >= 60 && (
                          <span className="trc-block-away">{tr("Away")} {Math.round(b.awaySeconds / 60)} {tr("min")}</span>
                        )}
                        {b.excludedSeconds >= 60 && (
                          <span className="trc-block-personal">{tr("Personal")} {Math.round(b.excludedSeconds / 60)} {tr("min")}</span>
                        )}
                      </div>
                    );
                  })}
                  {drag && (
                    <div
                      className="trc-selection"
                      style={{ top: ((Math.min(drag.a, drag.b) - winStart) / 60) * HOUR_PX, height: (Math.abs(drag.b - drag.a) / 60) * HOUR_PX }}
                    />
                  )}
                  {manualDraft && !drag && (
                    <div
                      className="trc-selection trc-selection--movable"
                      style={{ top: top(manualDraft.origStart), height: Math.max(((minutesOfDay(manualDraft.origEnd) - minutesOfDay(manualDraft.origStart)) / 60) * HOUR_PX, 6) }}
                      onMouseDown={startMove}
                      title={tr("Drag to move, or use the handles to resize")}
                    >
                      <div className="trc-handle trc-handle--top" onMouseDown={startResize('start')} title={tr("Drag to change the start")} />
                      <div className="trc-handle trc-handle--bottom" onMouseDown={startResize('end')} title={tr("Drag to change the end")} />
                    </div>
                  )}
                </div>
                <div className="trc-tl-col" style={{ height: timelineHeight }}>
                  <div className="trc-tl-title">{tr("Timesheet")}</div>
                  {data?.logged.map((l) => {
                    const h = height(l.start, l.end, TALL_MIN);
                    return (
                      <div key={l.timer_id} className={`trc-block trc-block--logged${h < TALL_ENOUGH ? ' trc-block--compact' : ''}`} style={{ top: top(l.start), height: h }}>
                        <b>{l.title || tr("(no description)")}</b>
                        {h >= TALL_ENOUGH && <span>{l.project?.project_name || tr("No project")} · {fmtDuration((new Date(l.end) - new Date(l.start)) / 1000)}</span>}
                      </div>
                    );
                  })}
                  {manualDraft && (
                    <button
                      type="button"
                      className={`trc-block trc-block--draft trc-block--movable${manualDraft.ready ? '' : ' trc-block--review'}${selectedId === 'manual' ? ' is-selected' : ''}${height(manualDraft.origStart, manualDraft.origEnd, TALL_MIN) < TALL_ENOUGH ? ' trc-block--compact' : ''}`}
                      style={{ top: top(manualDraft.origStart), height: height(manualDraft.origStart, manualDraft.origEnd, TALL_MIN) }}
                      onClick={() => setSelectedId('manual')}
                      onMouseDown={startMove}
                      title={tr("Drag to move, or use the handles to resize")}
                    >
                      <b>{tr("New entry")}</b>
                      {height(manualDraft.origStart, manualDraft.origEnd, TALL_MIN) >= TALL_ENOUGH && (
                        <span>{projects.find((p) => p.id === manualDraft.projectId)?.project_name || tr("No project yet")} · {fmtDuration((manualDraft.end - manualDraft.start) / 1000)}</span>
                      )}
                    </button>
                  )}
                  {manualDraft && (
                    <>
                      <div className="trc-handle trc-handle--float" style={{ top: top(manualDraft.origStart) - 6 }} onMouseDown={startResize('start')} title={tr("Drag to change the start")} />
                      <div className="trc-handle trc-handle--float" style={{ top: top(manualDraft.origStart) + height(manualDraft.origStart, manualDraft.origEnd, TALL_MIN) - 6 }} onMouseDown={startResize('end')} title={tr("Drag to change the end")} />
                    </>
                  )}
                  {drafts.map((d) => {
                    const h = height(d.origStart, d.origEnd, TALL_MIN);
                    return (
                      <button
                        type="button"
                        key={d.id}
                        className={`trc-block trc-block--draft${d.ready ? '' : ' trc-block--review'}${d.id === selectedId ? ' is-selected' : ''}${h < TALL_ENOUGH ? ' trc-block--compact' : ''}`}
                        style={{ top: top(d.origStart), height: h }}
                        onClick={() => setSelectedId(d.id)}
                        title={`${d.title || tr("Suggested work session")} · ${fmtDuration((d.end - d.start) / 1000)}`}
                      >
                        <b>{d.title || tr("Suggested work session")}</b>
                        {h >= TALL_ENOUGH && (
                          <span>{projects.find((p) => p.id === d.projectId)?.project_name || tr("No project yet")} · {fmtDuration((d.end - d.start) / 1000)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {nowTop !== null && nowTop >= 0 && nowTop <= timelineHeight && (
                  <div className="trc-now" style={{ top: nowTop + 34 }}><span>{tr("now")}</span></div>
                )}
              </div>
            )}
          </section>

          {/* ── Review panel ── */}
          <aside className="trc-side">
            <section className="trc-card trc-review">
              {!selected ? (
                <div className="trc-empty">
                  <b>{drafts.length === 0 && !loading ? tr("All caught up") : tr("Select a draft")}</b>
                  {drafts.length === 0 && !loading
                    ? tr("Trace drafts new blocks after about 20 minutes of focused work.")
                    : tr("Pick a striped block to review it, or click a captured block, or drag on the timeline, to log time yourself.")}
                </div>
              ) : (
                <>
                  <div className="trc-review-top">
                    <div>
                      <span className="trc-kicker">{selected.isManual ? tr("New entry") : tr("Draft")} · {fmtClock(selected.origStart)} – {fmtClock(selected.origEnd)}</span>
                      <span className={`trc-status ${selected.ready ? 'trc-status--ready' : 'trc-status--review'}`}>
                        <i />{selected.ready ? tr("Ready to log") : selected.projectId ? tr("Check the details") : tr("Needs a project")}
                      </span>
                    </div>
                    {!selected.isManual && <div className="trc-ring" style={{ '--p': Math.round((selected.confidence_score || 0) * 100) }} title={tr("How sure Trace is about this block")}>
                      <div>{Math.round((selected.confidence_score || 0) * 100)}%<small>{tr("SURE")}</small></div>
                    </div>}
                  </div>

                  <label className="trc-label" htmlFor="trc-title">{tr("Description")}</label>
                  <input
                    id="trc-title"
                    className="trc-input"
                    value={selected.title}
                    onChange={(e) => setEdit(selected.id, { title: e.target.value })}
                  />

                  <span className="trc-label">{tr("Project")}</span>
                  <div className="trc-projects">
                    {(() => {
                      const suggestedId = selected.suggested_project_id;
                      const list = [...projects].sort((a, b) => (a.id === suggestedId ? -1 : b.id === suggestedId ? 1 : 0)).slice(0, 8);
                      return list.map((p) => (
                        <button
                          type="button"
                          key={p.id}
                          className={`trc-proj${selected.projectId === p.id ? ' is-on' : ''}`}
                          onClick={() => setEdit(selected.id, { projectId: p.id })}
                        >
                          <i style={{ background: p.project_color || '#8b5cf6' }} />
                          {p.project_name}
                          {suggestedId === p.id && <em>{tr("AI")}</em>}
                        </button>
                      ));
                    })()}
                    {projects.length === 0 && <span className="trc-muted">{tr("No projects yet — create one first.")}</span>}
                  </div>
                  {(selected.source_summary?.project_hint_reason || selected.confidence_explanation) && (
                    <p className="trc-why">
                      <span>✦</span>
                      {selected.source_summary?.project_hint_reason || selected.confidence_explanation}
                    </p>
                  )}

                  <span className="trc-label">{tr("Time")}</span>
                  <div className="trc-times">
                    <input
                      className="trc-input"
                      type="time"
                      aria-label={tr("Start")}
                      value={fmtClock(selected.start)}
                      onChange={(e) => e.target.value && setEdit(selected.id, { start: e.target.value })}
                    />
                    <input
                      className="trc-input"
                      type="time"
                      aria-label={tr("End")}
                      value={fmtClock(selected.end)}
                      onChange={(e) => e.target.value && setEdit(selected.id, { end: e.target.value })}
                    />
                    <strong className="trc-dur">{selected.validRange ? fmtDuration((selected.end - selected.start) / 1000) : '—'}</strong>
                  </div>
                  <p className={`trc-hint${selected.validRange ? '' : ' is-bad'}`}>
                    {selected.isManual
                      ? (selected.validRange
                        ? tr("Drag the top or bottom edge on the timeline, or type the times. You cannot overlap an entry or log the future.")
                        : tr("Pick a valid time: at least 1 minute, within this day, and not in the future."))
                      : (selected.validRange
                        ? tr("Trim to leave out a break. You can shorten a block, not extend it.")
                        : `Keep the time within ${fmtClock(selected.origStart)} – ${fmtClock(selected.origEnd)}.`)}
                  </p>

                  {selected.isManual && (
                    <button type="button" className="trc-linkbtn" onClick={fitToActivity}>{tr("Fit to captured activity")}</button>
                  )}

                  <span className="trc-label">{tr("Seen in this block")}</span>
                  <div className="trc-sigs">
                    {(() => {
                      const apps = selected.top_apps || [];
                      const doms = selected.top_domains || [];
                      const total = [...apps, ...doms].reduce((sum, s) => sum + (s.seconds || 0), 0);
                      const pct = (seconds) => (total > 0 ? Math.round(((seconds || 0) / total) * 100) : null);
                      return (
                        <>
                          {apps.map((a) => (
                            <span key={a.app_name} className="trc-sig">
                              {a.app_name}{pct(a.seconds) !== null && <em className="trc-sig-pct"> {pct(a.seconds)}%</em>}
                            </span>
                          ))}
                          {doms.map((d) => (
                            <span key={d.domain} className="trc-sig trc-sig--dom">
                              {d.domain}{pct(d.seconds) !== null && <em className="trc-sig-pct"> {pct(d.seconds)}%</em>}
                            </span>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                  {(selected.top_window_titles || []).length > 0 && (
                    <ul className="trc-titles">
                      {selected.top_window_titles.slice(0, 3).map((t) => (
                        <li key={t.window_title} className="trc-title-line" title={t.window_title}>{t.window_title}</li>
                      ))}
                    </ul>
                  )}

                  <div className="trc-actions">
                    <button type="button" className="trc-btn trc-btn--primary" disabled={!selected.ready || busy} onClick={handleLog}>
                      {busy ? tr("Logging…") : selected.projectId ? tr("Log time") : tr("Pick a project first")}
                    </button>
                    <button type="button" className="trc-btn trc-btn--ghost" disabled={busy} onClick={handleDismiss}>{selected.isManual ? tr("Cancel") : tr("Dismiss")}</button>
                  </div>
                </>
              )}
            </section>

            <section className="trc-card">
              <div className="trc-card-head"><h2>{tr("Drafts")}</h2><small>{drafts.length ? `${drafts.length} pending` : ''}</small></div>
              <div className="trc-list">
                {drafts.length === 0 && <div className="trc-empty trc-empty--small">{tr("Nothing left to review 🎉")}</div>}
                {drafts.map((d) => (
                  <button type="button" key={d.id} className={`trc-li${d.id === selectedId ? ' is-selected' : ''}`} onClick={() => setSelectedId(d.id)}>
                    <span className="trc-li-text">
                      <b>{d.title || tr("Suggested work session")}</b>
                      <small>{fmtClock(d.start)} – {fmtClock(d.end)} · {fmtDuration((d.end - d.start) / 1000)}{projects.find((p) => p.id === d.projectId) ? ` · ${projects.find((p) => p.id === d.projectId).project_name}` : ''}</small>
                    </span>
                    <span className={`trc-mini ${d.ready ? 'trc-mini--ready' : 'trc-mini--review'}`}>{d.ready ? tr("READY") : tr("REVIEW")}</span>
                  </button>
                ))}
              </div>
              <div className="trc-bulk">
                <small>{readyDrafts.length ? `${readyDrafts.length} ready · one click` : tr("Nothing ready to bulk-log")}</small>
                <button type="button" className="trc-btn trc-btn--primary trc-btn--sm" disabled={!readyDrafts.length || busy} onClick={handleLogAllReady}>
                  {readyDrafts.length ? `Log ${readyDrafts.length} ready` : tr("Log all ready")}
                </button>
              </div>
            </section>
          </aside>
        </div>
          </>
        )}
      </div>
      <div className={`trc-toast${toast ? ' is-on' : ''}`} role="status" aria-live="polite">{toast}</div>
      <Footer />
    </div>
  );
}
